import PropTypes from "prop-types";
import { useEffect, useMemo, useRef } from "react";
import { usePrevious } from "Shared/util/custom-hooks";
import FormCard, {
    FormCardStatusTypes,
} from "Shared/components/cards/form-card/form-card";
import { RadioGroup } from "Shared/components/radio-group";
import TextChildComponent from "./child-components/text-child-component";

const RADIO_KIND = "Radio";

const resolveOptions = (number, texts, languageCode) => {
    const options = [];
    for (let i = 1; i <= number; i += 1) {
        options.push({ label: texts[languageCode][i], value: i.toString() });
    }
    return options;
};

const childComponentEnabled = (answer, enabledOn) =>
    enabledOn && enabledOn.includes(answer);

const RadioFormCard = ({
    languageCode,
    questionId,
    valid,
    disabled,
    headerTexts,
    answer,
    onAnswerChange,
    numberOfOptions,
    optionsTexts,
    childComponent,
    className,
}) => {
    const {
        kind: childComponentKind,
        enabledOn: childComponentEnabledOn,
        answer: childComponentAnswer,
        texts: childComponentTexts,
    } = childComponent || {};

    const showChildComponent = useMemo(
        () => childComponentEnabled(answer, childComponentEnabledOn),
        [answer, childComponentEnabledOn],
    );
    const childComponentText = useMemo(
        () => childComponentTexts && childComponentTexts[languageCode],
        [childComponentTexts, languageCode],
    );
    const radioOptions = useMemo(
        () => resolveOptions(numberOfOptions, optionsTexts, languageCode),
        [numberOfOptions, optionsTexts, languageCode],
    );

    const textChildComponentInputRef = useRef();
    const prevAnswer = usePrevious(answer);
    useEffect(() => {
        if (
            prevAnswer !== undefined &&
            answer !== prevAnswer &&
            textChildComponentInputRef.current
        ) {
            textChildComponentInputRef.current.focus();
        }
    }, [prevAnswer, answer, textChildComponentInputRef.current]);

    const handleAnswerChanged = (newAnswer) => {
        const answerRequireChildComponent = childComponentEnabled(
            newAnswer,
            childComponentEnabledOn,
        );
        if (answerRequireChildComponent) {
            onAnswerChange(
                questionId,
                RADIO_KIND,
                newAnswer,
                childComponentEnabledOn,
                childComponentKind,
                childComponentAnswer,
            );
        } else {
            onAnswerChange(questionId, RADIO_KIND, newAnswer);
        }
    };

    const handleChildComponentAnswerChanged = (newChildComponentAnswer) => {
        onAnswerChange(
            questionId,
            RADIO_KIND,
            answer,
            childComponentEnabledOn,
            childComponentKind,
            newChildComponentAnswer,
        );
    };

    return (
        <FormCard
            status={
                valid ? FormCardStatusTypes.VALID : FormCardStatusTypes.INVALID
            }
            header={headerTexts[languageCode]}
            className={className}
            content={
                <>
                    <RadioGroup
                        options={radioOptions}
                        selectedOption={answer}
                        disabled={disabled}
                        onChange={handleAnswerChanged}
                    />
                    {showChildComponent && (
                        <TextChildComponent
                            ref={textChildComponentInputRef}
                            answer={childComponentAnswer}
                            label={childComponentText}
                            disabled={disabled}
                            onAnswerChange={handleChildComponentAnswerChanged}
                        />
                    )}
                </>
            }
        />
    );
};

RadioFormCard.propTypes = {
    languageCode: PropTypes.string.isRequired,
    questionId: PropTypes.string.isRequired,
    valid: PropTypes.bool.isRequired,
    disabled: PropTypes.bool.isRequired,
    headerTexts: PropTypes.object.isRequired,
    answer: PropTypes.string,
    onAnswerChange: PropTypes.func.isRequired,
    numberOfOptions: PropTypes.number.isRequired,
    optionsTexts: PropTypes.object.isRequired,
    childComponent: PropTypes.oneOfType([
        // Text
        PropTypes.exact({
            kind: PropTypes.string.isRequired,
            enabledOn: PropTypes.array.isRequired,
            answer: PropTypes.string,
            texts: PropTypes.object,
        }),
    ]),
    className: PropTypes.string,
};

export default RadioFormCard;
